// TypingRuleForm.jsx - Updated for Dynamic Mistake System
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaPlus, FaTrash, FaSave } from 'react-icons/fa';
import { sendApiRequest } from '../../utils/api';

const TypingRuleForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [examCategories, setExamCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    examCategory: '',
    fullMistakes: [{ type: '', description: '', penalty: 1.0 }],
    halfMistakes: [{ type: '', description: '', penalty: 0.5 }],
    speedUnit: 'wpm',
    passingAccuracy: 95,
    isActive: true,
    notes: ''
  });

  const [errors, setErrors] = useState({});
  const [saveMessage, setSaveMessage] = useState('');

  useEffect(() => {
    fetchExamCategories();
    if (isEditing) {
      fetchRule();
    }
  }, [isEditing]);

  const fetchExamCategories = async () => {
    try {
      const response = await sendApiRequest('api/exam-categories', {}, 'GET');
      if (response) {
        setExamCategories(response);
      }
    } catch (error) {
      console.error('Error fetching exam categories:', error);
    }
  };

  const fetchRule = async () => {
    try {
      setLoading(true);
      const response = await sendApiRequest(`api/marking-rules/${id}`, {}, 'GET');
      if (response) {
        setFormData({
          title: response.title || '',
          examCategory: response.examCategory?._id || '',
          fullMistakes: response.fullMistakes?.length ? response.fullMistakes : [{ type: '', description: '', penalty: 1.0 }],
          halfMistakes: response.halfMistakes?.length ? response.halfMistakes : [{ type: '', description: '', penalty: 0.5 }],
          speedUnit: response.speedUnit || 'wpm',
          passingAccuracy: response.passingAccuracy || 95,
          isActive: response.isActive !== false,
          notes: response.notes || ''
        });
      }
    } catch (error) {
      console.error('Error fetching rule:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const addMistake = (type) => {
    const newMistake = { type: '', description: '', penalty: type === 'full' ? 1.0 : 0.5 };
    setFormData(prev => ({
      ...prev,
      [`${type}Mistakes`]: [...prev[`${type}Mistakes`], newMistake]
    }));
  };

  const removeMistake = (type, index) => {
    setFormData(prev => ({
      ...prev,
      [`${type}Mistakes`]: prev[`${type}Mistakes`].filter((_, i) => i !== index)
    }));
  };

  const updateMistake = (type, index, field, value) => {
    setFormData(prev => ({
      ...prev,
      [`${type}Mistakes`]: prev[`${type}Mistakes`].map((mistake, i) =>
        i === index ? { ...mistake, [field]: value } : mistake
      )
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.examCategory) newErrors.examCategory = 'Exam category is required';
    if (!formData.passingAccuracy || formData.passingAccuracy < 0 || formData.passingAccuracy > 100) {
      newErrors.passingAccuracy = 'Passing accuracy must be between 0 and 100';
    }

    // Validate full mistakes
    const validFullMistakes = formData.fullMistakes.filter(m => m.type.trim() && m.description.trim());
    if (validFullMistakes.length === 0) {
      newErrors.fullMistakes = 'At least one full mistake definition is required';
    }

    // Validate half mistakes
    const validHalfMistakes = formData.halfMistakes.filter(m => m.type.trim() && m.description.trim());
    if (validHalfMistakes.length === 0) {
      newErrors.halfMistakes = 'At least one half mistake definition is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setSaving(true);
    try {
      // Filter out empty mistakes
      const cleanData = {
        ...formData,
        fullMistakes: formData.fullMistakes.filter(m => m.type.trim() && m.description.trim()),
        halfMistakes: formData.halfMistakes.filter(m => m.type.trim() && m.description.trim())
      };

      const method = isEditing ? 'PATCH' : 'POST';
      const url = isEditing ? `api/marking-rules/${id}` : 'api/marking-rules';

      await sendApiRequest(url, cleanData, method);

      setSaveMessage(isEditing ? 'Rule updated successfully!' : 'Rule created successfully!');

      setTimeout(() => {
        navigate('/admin/marking-rules');
      }, 1500);

    } catch (error) {
      console.error('Error saving rule:', error);
      setSaveMessage('Error saving rule. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">
          {isEditing ? 'Edit' : 'Create'} Typing Rule
        </h2>
        <p className="text-gray-600 mt-1">
          Define mistake types and penalties for different exam categories
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rule Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                errors.title ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="e.g., SSC Stenography Rules"
            />
            {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Exam Category *
            </label>
            <select
              value={formData.examCategory}
              onChange={(e) => handleInputChange('examCategory', e.target.value)}
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                errors.examCategory ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Select Exam Category</option>
              {examCategories.map(category => (
                <option key={category._id} value={category._id}>
                  {category.title}
                </option>
              ))}
            </select>
            {errors.examCategory && <p className="text-red-500 text-sm mt-1">{errors.examCategory}</p>}
          </div>
        </div>

        {/* Speed and Accuracy Settings */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Speed Unit
            </label>
            <select
              value={formData.speedUnit}
              onChange={(e) => handleInputChange('speedUnit', e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="wpm">Words Per Minute (WPM)</option>
              <option value="kpm">Keystrokes Per Minute (KPM)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Passing Accuracy (%) *
            </label>
            <input
              type="number"
              min="0"
              max="100"
              value={formData.passingAccuracy}
              onChange={(e) => handleInputChange('passingAccuracy', parseInt(e.target.value) || 0)}
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                errors.passingAccuracy ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.passingAccuracy && <p className="text-red-500 text-sm mt-1">{errors.passingAccuracy}</p>}
          </div>
        </div>

        {/* Full Mistakes Section */}
        <div className="bg-red-50 p-4 rounded-lg">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-red-800">Full Mistakes (1.0 penalty)</h3>
            <button
              type="button"
              onClick={() => addMistake('full')}
              className="flex items-center px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              <FaPlus className="mr-2" /> Add Full Mistake
            </button>
          </div>

          {formData.fullMistakes.map((mistake, index) => (
            <div key={index} className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-3 p-3 bg-white rounded border">
              <div className="md:col-span-3">
                <input
                  type="text"
                  placeholder="Mistake type"
                  value={mistake.type}
                  onChange={(e) => updateMistake('full', index, 'type', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded"
                />
              </div>
              <div className="md:col-span-6">
                <input
                  type="text"
                  placeholder="Description"
                  value={mistake.description}
                  onChange={(e) => updateMistake('full', index, 'description', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded"
                />
              </div>
              <div className="md:col-span-2">
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  placeholder="Penalty"
                  value={mistake.penalty}
                  onChange={(e) => updateMistake('full', index, 'penalty', parseFloat(e.target.value) || 0)}
                  className="w-full p-2 border border-gray-300 rounded"
                />
              </div>
              <div className="md:col-span-1 flex items-center">
                <button
                  type="button"
                  onClick={() => removeMistake('full', index)}
                  className="p-2 text-red-600 hover:text-red-800"
                  disabled={formData.fullMistakes.length === 1}
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          ))}
          {errors.fullMistakes && <p className="text-red-500 text-sm mt-2">{errors.fullMistakes}</p>}
        </div>

        {/* Half Mistakes Section */}
        <div className="bg-yellow-50 p-4 rounded-lg">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-yellow-800">Half Mistakes (0.5 penalty)</h3>
            <button
              type="button"
              onClick={() => addMistake('half')}
              className="flex items-center px-3 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
            >
              <FaPlus className="mr-2" /> Add Half Mistake
            </button>
          </div>

          {formData.halfMistakes.map((mistake, index) => (
            <div key={index} className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-3 p-3 bg-white rounded border">
              <div className="md:col-span-3">
                <input
                  type="text"
                  placeholder="Mistake type"
                  value={mistake.type}
                  onChange={(e) => updateMistake('half', index, 'type', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded"
                />
              </div>
              <div className="md:col-span-6">
                <input
                  type="text"
                  placeholder="Description"
                  value={mistake.description}
                  onChange={(e) => updateMistake('half', index, 'description', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded"
                />
              </div>
              <div className="md:col-span-2">
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  placeholder="Penalty"
                  value={mistake.penalty}
                  onChange={(e) => updateMistake('half', index, 'penalty', parseFloat(e.target.value) || 0)}
                  className="w-full p-2 border border-gray-300 rounded"
                />
              </div>
              <div className="md:col-span-1 flex items-center">
                <button
                  type="button"
                  onClick={() => removeMistake('half', index)}
                  className="p-2 text-red-600 hover:text-red-800"
                  disabled={formData.halfMistakes.length === 1}
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          ))}
          {errors.halfMistakes && <p className="text-red-500 text-sm mt-2">{errors.halfMistakes}</p>}
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Additional Notes
          </label>
          <textarea
            value={formData.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            rows={3}
            placeholder="Special rules or additional information..."
          />
        </div>

        {/* Submit Button */}
        <div className="flex justify-end space-x-3 pt-6">
          <button
            type="button"
            onClick={() => navigate('/admin/marking-rules')}
            className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <FaSave className="mr-2" />
                {isEditing ? 'Update' : 'Create'} Rule
              </>
            )}
          </button>
        </div>

        {saveMessage && (
          <div className={`p-3 rounded-lg text-center ${
            saveMessage.includes('Error')
              ? 'bg-red-100 text-red-700'
              : 'bg-green-100 text-green-700'
          }`}>
            {saveMessage}
          </div>
        )}
      </form>
    </div>
  );
};

export default TypingRuleForm;
